import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { interactionsApi } from '@/lib/api';
import { Building2, ChevronRight, CircleAlert, Edit3, Trash2, User, Users, type LucideIcon } from 'lucide-react';
import {
  ACTIVITY,
  Angaben,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Faelligkeit,
  Icon,
  Skeleton,
  Status,
  TONE_PILL,
  cn,
  formatDatum,
  formatZeit,
  interaktionInfo,
} from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { InteractionSheet } from './components/InteractionSheet';
import { ActivityTimeline } from './components/ActivityTimeline';
import { wiedervorlageAus } from './components/WiedervorlageFeld';

/** Eine Verknüpfung in der Seitenleiste: Icon, Art, Name, Pfeil. */
function Verknuepfung({ to, icon, art, name }: { to: string; icon: LucideIcon; art: string; name: string }) {
  return (
    <Link to={to} className="group flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-accent">
      <Icon icon={icon} size={18} className="text-highlight" />
      <div className="min-w-0 flex-1">
        <div className="t-label text-muted-foreground">{art}</div>
        <div className="truncate t-body-strong">{name}</div>
      </div>
      <ChevronRight size={16} strokeWidth={1.75} className="text-muted-foreground" aria-hidden />
    </Link>
  );
}

export function InteractionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: interaction, isLoading, error } = useQuery({
    queryKey: ['interactions', id],
    queryFn: () => interactionsApi.get(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => interactionsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactions'] });
      navigate('/interactions');
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6 pt-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] w-full rounded-lg" />
      </div>
    );
  }

  if (error || !interaction) {
    return (
      <>
        <CrmPageHeader title="Interaktion" zurueck={{ to: '/interactions', label: 'Zur Übersicht' }} />
        <EmptyState
          icon={CircleAlert}
          title="Interaktion nicht gefunden"
          action={<Button onClick={() => navigate('/interactions')}>Zu den Interaktionen</Button>}
        />
      </>
    );
  }

  const info = interaktionInfo(interaction.type);
  const art = ACTIVITY[info.type];

  // Wiedervorlage: Fälligkeit und Stand der Aufgabe „Nachfassen“, die das
  // Backend dazu angelegt hat, mit Weg dorthin. Ohne Aufgabe (gelöscht) nur das Datum.
  const aufgabeId = interaction.wiedervorlage_aufgabe_id;
  const aufgabeStatus = interaction.wiedervorlage_aufgabe_status;
  // Faelligkeit kürzt einen Zeitstempel auf den UTC-Tag; ersatzweise also der lokale Tag.
  const faellig = interaction.wiedervorlage_aufgabe_faellig || wiedervorlageAus(interaction.follow_up_date);
  const wiedervorlage = interaction.follow_up_date && (
    <span className="flex flex-col items-start gap-1">
      {aufgabeId && aufgabeStatus && faellig ? (
        <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
          <Faelligkeit
            datum={faellig}
            erledigt={aufgabeStatus === 'completed' || aufgabeStatus === 'cancelled'}
          />
          <Status status={`task_${aufgabeStatus}`} variant="plain" />
        </span>
      ) : (
        <span className="t-mono tabular">{formatDatum(interaction.follow_up_date)}</span>
      )}
      {/* Nur mit Stand: Ohne ihn ist die Aufgabe gelöscht, der Link ginge ins Leere. */}
      {aufgabeId && aufgabeStatus && (
        <Link
          to={`/tasks/${aufgabeId}`}
          className="inline-flex items-center t-small font-medium text-link hover:underline touch:min-h-9"
        >
          Aufgabe öffnen
        </Link>
      )}
    </span>
  );

  return (
    <div className="space-y-6">
      <CrmPageHeader
        size="page"
        zurueck={{ to: '/interactions', label: 'Zur Übersicht' }}
        title={
          <span className="inline-flex items-center gap-3">
            <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-full', TONE_PILL[art.tone])}>
              <Icon icon={art.icon} size={17} />
            </span>
            {interaction.subject}
          </span>
        }
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-2">
            <span>{info.label}</span>·
            <span>
              {formatDatum(interaction.occurred_at, { lang: true })}, {formatZeit(interaction.occurred_at)}
            </span>
          </span>
        }
        mehrAktionen={[
          { label: 'Bearbeiten', icon: Edit3, onSelect: () => setIsEditing(true) },
          { label: 'Interaktion löschen', icon: Trash2, tone: 'danger', onSelect: () => setShowDeleteConfirm(true) },
        ]}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          <Card title="Beschreibung">
            {interaction.description ? (
              <p className="whitespace-pre-wrap">{interaction.description}</p>
            ) : (
              <p className="text-muted-foreground">Keine Beschreibung</p>
            )}
          </Card>

          {(interaction.customer_id || interaction.partner_id) && (
            <Card
              title={`Gesamte Timeline ${interaction.customer_name ? `– ${interaction.customer_name}` : interaction.partner_name ? `– ${interaction.partner_name}` : ''}`}
            >
              <ActivityTimeline
                entityType={interaction.customer_id ? 'customer' : 'partner'}
                entityId={(interaction.customer_id || interaction.partner_id)!}
                onAddInteraction={() => setIsEditing(true)}
              />
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card title="Verknüpfungen">
            <div className="-mx-3 space-y-1">
              {interaction.customer_id && (
                <Verknuepfung to={`/customers/${interaction.customer_id}`} icon={Building2} art="Kunde" name={interaction.customer_name || 'Kunde anzeigen'} />
              )}
              {interaction.partner_id && (
                <Verknuepfung to={`/partners/${interaction.partner_id}`} icon={Users} art="Partner" name={interaction.partner_name || 'Partner anzeigen'} />
              )}
              {interaction.contact_id && (
                <Verknuepfung to={`/contacts/${interaction.contact_id}`} icon={User} art="Ansprechpartner" name={interaction.contact_name || 'Kontakt anzeigen'} />
              )}
              {!interaction.customer_id && !interaction.partner_id && !interaction.contact_id && (
                <p className="px-3 t-small text-muted-foreground">Keine Verknüpfungen</p>
              )}
            </div>
          </Card>

          <Card title="Info">
            <Angaben
              items={[
                { label: 'Erstellt am', value: formatDatum(interaction.created_at), mono: true },
                { label: 'Aktualisiert', value: formatDatum(interaction.updated_at), mono: true },
                // Ergebnis und Dauer zeigt die Seite nicht mehr (Henri, 26.10.2026: historisch
                // gewachsen, nirgends eintragbar, in Produktion nie gefüllt). Die Wiedervorlage
                // nur, wenn eine gesetzt ist; eintragen lässt sie sich im Formular.
                ...(wiedervorlage ? [{ label: 'Wiedervorlage', value: wiedervorlage }] : []),
              ]}
            />
          </Card>
        </div>
      </div>

      <InteractionSheet
        open={isEditing}
        onOpenChange={setIsEditing}
        title="Interaktion bearbeiten"
        interaction={{
          id: interaction.id,
          type: interaction.type,
          subject: interaction.subject,
          description: interaction.description ?? '',
          occurred_at: interaction.occurred_at,
          follow_up_date: interaction.follow_up_date,
        }}
        customerId={interaction.customer_id ?? undefined}
        partnerId={interaction.partner_id ?? undefined}
        contactId={interaction.contact_id ?? undefined}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Interaktion löschen?"
        description={`„${interaction.subject}“ wird endgültig gelöscht.`}
        confirmLabel="Interaktion löschen"
        tone="danger"
        onConfirm={() => deleteMutation.mutateAsync()}
      />
    </div>
  );
}
